import { EditBtn } from 'components/button/EditBtn/EditBtn'
import { Icon } from 'components/icon/Icon'
import { Tooltip, TooltipProps } from 'components/tooltip/Tooltip'
import { Icons } from 'contents/icons'
import React, { useEffect, useRef, useState } from 'react'
import { getTranslation } from 'utils/translation'
import { StyledTextField } from './style'

/**
 * 텍스트 입력필드 컴포넌트 속성
 */
export interface TextFieldProps {
  size: 'large' | 'medium' | 'small'
  value: string
  width?: string
  label?: string
  type?: 'text' | 'email' | 'password' | 'url'
  id?: string
  name?: string
  className?: string
  icon?: keyof typeof Icons
  postIcon?: keyof typeof Icons
  infoDetail?: string
  defaultValue?: string
  placeholder?: string
  helpMessage?: string
  maxLength?: number
  minLength?: number
  lengthCount?: boolean
  required?: boolean
  readOnly?: boolean
  disabled?: boolean
  success?: boolean
  warning?: boolean
  error?: boolean
  deleteButton?: boolean
  autoFocus?: boolean
  tooltip?: TooltipProps
  handleValue?: (value: string) => void
  handleEnterValue?: (value: string) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onKeyUp?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseUp?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseEnter?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave?: (e: React.MouseEvent<HTMLDivElement>) => void
  onMount?: () => void
  onUnmount?: () => void
}

/**
 * TextField component
 * @author hc_yang
 * @param {TextFieldProps} param
 * @returns JSX.Element
 */
export const TextField = ({
  size,
  value,
  width = '100%',
  label,
  type = 'text',
  id,
  name,
  className,
  icon,
  postIcon,
  infoDetail,
  defaultValue,
  placeholder,
  helpMessage,
  maxLength,
  minLength,
  lengthCount,
  required = false,
  readOnly = false,
  disabled = false,
  success = false,
  warning = false,
  error = false,
  deleteButton = true,
  autoFocus = false,
  tooltip,
  handleValue,
  handleEnterValue,
  onBlur,
  onFocus,
  onChange,
  onKeyDown,
  onKeyUp,
  onClick,
  onMouseDown,
  onMouseUp,
  onMouseEnter,
  onMouseLeave,
  onMount,
  onUnmount,
}: TextFieldProps) => {
  const PreIcon = icon && Icons[icon]
  const PostIcon = postIcon && Icons[postIcon]
  const parentRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const timeoutID = useRef<NodeJS.Timeout>()
  const infoRef = useRef<HTMLDivElement | null>(null)

  const [isHovered, setIsHovered] = useState(false)
  const [isInfoHovered, setIsInfoHovered] = useState(false)
  const [errorAnimation, setErrorAnimation] = useState(false)
  const [isMaxLengthError, setIsMaxLengthError] = useState(false)
  const [isMinLengthError, setIsMinLengthError] = useState(false)
  const [touched, setTouched] = useState(false)
  const [passwordVisible, setPasswordVisible] = useState(false)

  const inputFocus = () => {
    if (inputRef.current) inputRef.current.focus()
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    !touched && setTouched(true)
    onBlur && onBlur(e)
    checkLengthError(value)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    if (onChange) onChange(e)
    if (!checkLengthError(inputValue)) return
    if (handleValue) handleValue(inputValue)
  }

  const handleDelete = () => {
    inputFocus()
    if (handleValue) handleValue('')
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleEnterValue && handleEnterValue(value)
    if (onKeyUp) onKeyUp(e)
  }

  const handlePasswordVisible = () => setPasswordVisible(!passwordVisible)

  const checkLengthError = (inputValue: string) => {
    if (maxLength !== undefined && maxLength < inputValue.length) {
      setIsMaxLengthError(true)
      setErrorAnimation(true)

      return false
    } else {
      setIsMaxLengthError(false)
    }
    if (minLength !== undefined && minLength > inputValue.length) {
      setIsMinLengthError(true)
    } else {
      setIsMinLengthError(false)
    }
    return true
  }

  const onWheelHandler = (e: React.WheelEvent<HTMLInputElement>) => {
    if (e.deltaY !== 0 && inputRef && inputRef.current) {
      inputRef.current.scrollLeft += e.deltaY
    }
  }

  useEffect(() => {
    if (error && touched) setErrorAnimation(true)
  }, [error])

  useEffect(() => {
    if (timeoutID.current) clearTimeout(timeoutID.current)
    timeoutID.current = setTimeout(() => setErrorAnimation(false), 500)
  }, [errorAnimation])

  useEffect(() => {
    onMount && onMount()
    return () => {
      onUnmount && onUnmount()
    }
  }, [])

  return (
    <StyledTextField
      ref={parentRef}
      $size={size}
      $width={width}
      $readOnly={readOnly}
      $disabled={disabled}
      $success={success}
      $warning={warning}
      $error={isMaxLengthError || isMinLengthError || error}
      className={className}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      onMouseEnter={(e) => {
        setIsHovered(true)
        onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        setIsHovered(false)
        onMouseLeave?.(e)
      }}
    >
      {label && (
        <div className="label-area">
          <p className="label">{label}</p>
          {required && <p className="required">*</p>}
          {infoDetail && (
            <div
              className="info"
              ref={infoRef}
              onMouseEnter={() => setIsInfoHovered(true)}
              onMouseLeave={() => setIsInfoHovered(false)}
            >
              <Icon
                icon="InfoCircle"
                size={10}
                style={{ padding: size === 'large' ? '0.1rem' : '0' }}
                stroke="--text-gray-4"
              />
              {isInfoHovered && <Tooltip parentRef={infoRef} direction="left" label={infoDetail} />}
            </div>
          )}
        </div>
      )}

      <div className={`form-area ${errorAnimation ? 'animation' : ''}`}>
        {PreIcon && <PreIcon className="icon" />}
        <input
          ref={inputRef}
          type={passwordVisible ? 'text' : type}
          data-focus="true"
          name={name}
          id={id}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onKeyUp={handleKeyUp}
          onBlur={handleBlur}
          onFocus={onFocus}
          onWheel={onWheelHandler}
          placeholder={!readOnly ? placeholder : undefined}
          disabled={disabled}
          readOnly={readOnly}
          defaultValue={defaultValue}
          autoFocus={autoFocus}
        />
        {deleteButton && !readOnly && !disabled && value.length > 0 && (
          <EditBtn
            className="del-btn"
            size={size === 'large' ? 'medium' : 'small'}
            type="delete"
            onClick={handleDelete}
            tabIndex={-1}
          />
        )}
        {PostIcon && <PostIcon className="icon" />}
        {type === 'password' && !readOnly && !disabled && (
          <button className="eye-btn" onClick={handlePasswordVisible} tabIndex={-1}>
            {passwordVisible ? <Icons.Eye className="icon" /> : <Icons.EyeOff className="icon" />}
          </button>
        )}
      </div>

      {(helpMessage !== undefined || lengthCount) && (
        <div className="info-area">
          <p className="help">
            {isMaxLengthError
              ? getTranslation('message', 'ACTION.MAX_LENGTH', [String(maxLength)])
              : isMinLengthError
                ? getTranslation('message', 'ACTION.MIN_LENGTH', [String(minLength)])
                : helpMessage}
          </p>
          {maxLength && lengthCount && (
            <span className="count">
              <b>{value.length}</b>/{maxLength}
            </span>
          )}
        </div>
      )}
      {isHovered && tooltip && <Tooltip {...tooltip} parentRef={parentRef} />}
    </StyledTextField>
  )
}
